import { FileUp, MoreHorizontal, Plus, RotateCw, Search } from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { PanelLoading } from "~/components/loading";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from "~/components/ui";
import type { DipColumnFilterValues } from "~/features/dataImportProjects/types";
import { DataImportProjectBootstrapDialog } from "./DataImportProjectBootstrapDialog";
import { DataImportProjectColumnFiltersConnected } from "./DataImportProjectColumnFiltersConnected";
import { DataImportProjectCreateDialog } from "./DataImportProjectCreateDialog";
import { DataImportProjectsTable } from "./DataImportProjectsTable";
import { EventNormalizationImportDialog } from "./EventNormalizationImportDialog";

interface DataImportProjectListState {
  search: string;
  excludeDeactivated: boolean;
  columnFilterValues: DipColumnFilterValues;
  pageIndex: number;
  pageSize: number;
}

interface DataImportProjectListPresenterProps {
  listState: DataImportProjectListState;
  onSearchChange: (next: string) => void;
  onApplyFilters: (next: DipColumnFilterValues, excludeDeactivated: boolean) => void;
  onPageIndexChange: (next: number) => void;
  onPageSizeChange: (next: number) => void;
}

export function DataImportProjectListPresenter({
  listState,
  onSearchChange,
  onApplyFilters,
  onPageIndexChange,
  onPageSizeChange,
}: DataImportProjectListPresenterProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  const [eventNormalizationImportOpen, setEventNormalizationImportOpen] = useState(false);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-dewey-title capitalize text-fg-1">{t("model.dataImportProject.modelNames")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
              <Input
                value={listState.search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t("ui.search")}
                className="h-9 w-[260px] pl-8"
                aria-label={t("ui.search")}
              />
            </div>
            <DataImportProjectColumnFiltersConnected
              value={listState.columnFilterValues}
              excludeDeactivated={listState.excludeDeactivated}
              onApply={onApplyFilters}
            />
            <Button
              type="submit"
              variant="outline"
              size="icon"
              aria-label={t("ui.reload")}
              form="dip-project-list-refresh"
            >
              <RotateCw />
            </Button>
            <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus />
              <span>{t("ui.create")}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEventNormalizationImportOpen(true)}>
                  <FileUp />
                  <span>{t("feature.dataImportProjects.eventNormalizationImport.action")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setBootstrapOpen(true)}>
                  <Plus />
                  <span>
                    {t("ui.bootstrap")} ({t("ui.deprecated")})
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Suspense fallback={<PanelLoading />}>
          <DataImportProjectsTable
            search={listState.search}
            excludeDeactivated={listState.excludeDeactivated}
            columnFilterValues={listState.columnFilterValues}
            pageIndex={listState.pageIndex}
            onPageIndexChange={onPageIndexChange}
            pageSize={listState.pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        </Suspense>
      </div>
      <DataImportProjectCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EventNormalizationImportDialog
        open={eventNormalizationImportOpen}
        onOpenChange={setEventNormalizationImportOpen}
      />
      <DataImportProjectBootstrapDialog open={bootstrapOpen} onOpenChange={setBootstrapOpen} />
    </div>
  );
}
